/**
 * Cliente tipado del API de la federación (`apps/api`).
 *
 * Criterios:
 *  - **Sólo endpoints REALES.** Cada método de acá existe en
 *    `apps/api/src/routes/`. Las proyecciones avanzadas (rotación, armador,
 *    zonas, recepción, evolución) NO tienen endpoint propio: se derivan en el
 *    cliente desde `GET /events` (ver `src/lib/projections.ts`).
 *  - **`fetch` inyectable**: los tests pasan un fake y no tocan la red.
 *  - **Errores traducidos**: 401/403/404/501 y los fallos de red se convierten
 *    en un `ApiError` con mensaje en el idioma activo listo para mostrar; nadie
 *    arriba tiene que interpretar códigos HTTP.
 *  - **Tipos**: los que el contrato ya publica (`MatchReport`, `MatchSummary`,
 *    `ScoutEvent`, `TeamInfo`) se importan como TIPO de `@scout/core` — no se
 *    ejecuta ni una línea de ese package en el browser. Los que sólo existen
 *    en `@scout/stats` (`SeasonReport`) se declaran acá copiando la forma: el
 *    portal consume JSON, no importa la proyección.
 */
import type {
  ChallengeState,
  Competition,
  CompetitionInput,
  FixtureMatch,
  MatchReport,
  MatchRole,
  MatchSummary,
  OfficialRef,
  PlayerReport,
  ScoutEvent,
  ScoringSystem,
  SheetDocument,
  SheetOutcome,
  SheetState,
  SkillLine,
  StandingsRow,
  TeamInfo,
  TeamSide,
  TiebreakCriterion,
} from '@scout/core';
import { getT } from '../i18n/index';
import type { BenchIntent, BenchSnapshot } from './bench';
import { previousRallyQuery, type PreviousRallyFilter } from './rally-filter';

// ---------------------------------------------------------------------------
// Tipos de respuesta que no viven en @scout/core
// ---------------------------------------------------------------------------

export interface HealthResponse {
  ok: boolean;
  version: string;
}

/** `GET /api/matches/:id/events?sinceSeq=` */
export interface EventsPage {
  matchId: string;
  sinceSeq: number | null;
  count: number;
  lastSeq: number;
  events: ScoutEvent[];
}

/**
 * Los dos documentos imprimibles de un partido, que NO son lo mismo:
 *  - `scoresheet` → el acta de mesa (`scoresheet.html`), para firmar;
 *  - `report`     → la estadística (`report.html`), para el entrenador.
 */
export type MatchDocument = 'scoresheet' | 'report';

// --- Clips (playlist del VAR) ----------------------------------------------

/**
 * Filtro de `GET /api/matches/:id/clips`. Todo opcional: lo que no viaja no
 * filtra. `rotation` es la lista de posiciones del armador (1..6) y necesita
 * saber de qué equipo — sale de `team` salvo que se pase `rotationTeam`.
 */
export interface ClipFilterQuery {
  team?: 'home' | 'away';
  player?: number;
  skill?: string;
  evaluation?: string;
  sets?: readonly number[];
  rotation?: readonly number[];
  rotationTeam?: 'home' | 'away';
  phase?: 'sideout' | 'break';
  phaseTeam?: 'home' | 'away';
  /** "One Rally Before": viaja como `prevWonBy`/`prevTouch*` (ver `rally-filter.ts`). */
  previousRally?: PreviousRallyFilter;
}

/**
 * Opciones comunes de los REPORTES del API (`/report`, `/season/report`):
 * la convención de eficiencia va por id (`?efficiency=`, validado en el borde)
 * y el "One Rally Before" con los mismos campos `prev*` que el filtro de clips.
 */
export interface ReportQueryOptions {
  /** Id de `EFFICIENCY_PRESETS` (`lib/efficiency.ts`); ausente = estándar. */
  efficiency?: string;
  previousRally?: PreviousRallyFilter;
}

/**
 * Un corte de la playlist. La forma la fija `@scout/stats` (`Clip`), que el
 * portal NO ejecuta: se declara acá copiándola, igual que `SeasonReport`. La
 * paridad la cuida `apps/portal/test/clips.test.ts`.
 */
export interface Clip {
  eventId: string;
  label: string;
  /** Timecode maestro del server de replay, en nanosegundos. */
  videoNs: number;
  preSeconds: number;
  postSeconds: number;
  team: 'home' | 'away';
  player: number | null;
  skill: string;
  evaluation: string;
  set: number;
  score: { home: number; away: number };
}

export interface ClipPlaylist {
  matchId: string;
  generatedAt: string;
  setsIncluded: number[] | 'all';
  clips: Clip[];
  /** Toques que matchearon el filtro pero no tenían timecode: no hay clip. */
  clipsSkipped: number;
}

export interface ClipPlaylistResponse {
  matchId: string;
  /** El filtro que el API entendió, para poder decir "0 clips PARA ESTO". */
  filter: Record<string, unknown>;
  data: ClipPlaylist;
}

// --- Import de `.dvw` histórico (ítem 2.7) ---------------------------------

/** Un archivo de la tanda, ya leído del disco del operador. */
export interface DvwImportFile {
  name: string;
  /** Bytes del archivo en base64 (ver `importDvw`). */
  content: string;
  encoding?: 'base64' | 'text';
}

/**
 * Por qué una línea del `.dvw` no se pudo traducir a un evento propio. Lo fija
 * el API (`tools/dvw-import.ts`) y el portal sólo lo traduce; la paridad la
 * cuida `apps/portal/test/import-dvw.test.ts`.
 */
export type DvwUnmappedKind = 'green_code' | 'setter_change' | 'sanction_no_team' | 'unknown_code';

export interface DvwUnmappedGroup {
  code: string;
  sample: string;
  count: number;
  kind: DvwUnmappedKind;
}

export interface DvwTeamPreview {
  side: 'home' | 'away';
  teamId: string;
  name: string;
  code: string | null;
  coach: string | null;
  players: number;
  liberos: number[];
}

export interface DvwFileReport {
  file: string;
  matchId: string;
  charset: 'utf-8' | 'latin-1' | null;
  matchName: string;
  competition: string | null;
  venue: string | null;
  scheduledAt: string | null;
  sportConfigId: string;
  teams: DvwTeamPreview[];
  sets: number;
  setsWon: { home: number; away: number };
  events: number;
  eventsByType: Record<string, number>;
  touches: number;
  points: number;
  unmapped: DvwUnmappedGroup[];
  unmappedCount: number;
  warnings: string[];
}

export type DvwFileOutcome = 'imported' | 'already_imported' | 'preview' | 'failed';

export interface DvwTeamOutcome {
  teamId: string;
  name: string;
  state: 'created' | 'merged' | 'unchanged' | 'skipped';
  addedPlayers: number[];
  totalPlayers: number;
}

export interface DvwFileResult {
  outcome: DvwFileOutcome;
  report: DvwFileReport | null;
  eventsWritten: number;
  teams: DvwTeamOutcome[];
  error: string | null;
}

export interface DvwImportSummary {
  imported: number;
  alreadyImported: number;
  preview: number;
  failed: number;
  events: number;
  teamsCreated: number;
  teamsMerged: number;
  unmapped: number;
}

export interface DvwImportResponse {
  dryRun: boolean;
  count: number;
  teamsWritable: boolean;
  summary: DvwImportSummary;
  files: DvwFileResult[];
}

// --- Fase D: consola de partido y cola de intents --------------------------

/** `GET /api/matches/:id/intents` — el delta desde el cursor anterior. */
export interface IntentsPage {
  matchId: string;
  /** Cursor NUEVO. Nunca retrocede, aunque el delta venga vacío. */
  intentVersion: number;
  count: number;
  intents: BenchIntent[];
}

/**
 * Cuerpo de `POST /intents`. Lo que el banco SABE y nada más: el marcador, el
 * set, quién saca y el ordinal del tiempo los deriva el server del estado — si
 * los mandara el banco, el acta registraría la versión del banco de un hecho
 * de mesa.
 */
export interface CreateIntentBody {
  /** Sólo si la designación no trae lado; cuando lo trae, gana la designación. */
  team?: 'home' | 'away';
  /** Timecode del PEDIDO (VCS art. 7: es el reloj que vale, no el del accept). */
  requestedAtNs?: number;
  deviceId?: string;
  intent:
    | { kind: 'timeout'; requestedBy?: 'COACH' | 'ASSISTANT_COACH' | 'GAME_CAPTAIN' }
    | { kind: 'substitution'; playerOut: number; playerIn: number }
    | {
        kind: 'lineup';
        setNumber: number;
        rotation: number[];
        setterPosition?: number;
        liberos?: number[];
      }
    | { kind: 'challenge'; claimedFault?: string | null };
}

/** Cuerpo del canje del QR (`POST /api/auth/match-login`). */
export interface MatchLoginBody {
  org: string;
  matchId: string;
  credentialId: string;
  token: string;
}

export interface MatchLoginResult {
  token: string;
  expiresAt?: string;
  match: { matchId: string; role: MatchRole; team?: 'home' | 'away'; holderName: string };
  /** Qué puede hacer esta credencial: la pantalla dibuja con esto. */
  permissions: string[];
  org: { id: string; name?: string; slug?: string };
}

/** Registro de la base de equipos (`TeamInfo` + identidad de archivo). */
export interface TeamRecord extends TeamInfo {
  id: string;
  updatedAt: string;
}

// --- Acta electrónica: la mesa de control (rutas de `routes/sheet.ts`) ------

/**
 * Una designación de partido, como sale por HTTP. La forma la fija
 * `toPublicAssignment` en `apps/api/src/store/match-assignment-store.ts` y se
 * copia acá SIN los hashes (que el API nunca manda): el portal consume JSON.
 */
export interface MatchCredential {
  matchId: string;
  /** `userId` del padrón o `cred_…` de una credencial descartable. */
  subjectId: string;
  kind: 'account' | 'credential';
  role: MatchRole;
  team?: TeamSide;
  /** Nombre del titular; es el que va al acta como firmante. */
  holderName: string;
  assignedBy: string;
  validFrom: string;
  validUntil?: string;
  revoked: boolean;
}

/** `GET /api/matches/:id/sheet` — el estado del DOCUMENTO y del challenge. */
export interface SheetResponse {
  matchId: string;
  /** Fase del JUEGO (`idle`…`finished`), no del documento. */
  phase: string;
  document: SheetState;
  challenge: ChallengeState;
  lastSeq: number;
  officials: OfficialRef[];
  assignments: MatchCredential[];
}

/** Cuerpo de `POST /credentials`. `subjectId` sólo con `kind: 'account'`. */
export interface IssueCredentialBody {
  kind?: 'credential' | 'account';
  role: MatchRole;
  team?: TeamSide;
  holderName: string;
  subjectId?: string;
  validUntil?: string;
}

/**
 * Lo que `POST /credentials` devuelve UNA sola vez. Con `kind: 'account'` no
 * hay secreto que mostrar: viene sólo `assignment`. Con una credencial QR+PIN
 * vienen el token (para el QR/link) y el PIN (se dicta al titular): no vuelven
 * a existir en claro en ningún lado, ni acá ni en el server.
 */
export interface IssuedCredentialResult {
  assignment: MatchCredential;
  credentialId?: string;
  token?: string;
  pin?: string;
  /** JSON `{matchId, credentialId, token}` listo para codificar en un QR. */
  qr?: string;
}

/** Cuerpo de `POST /sign`. El `secret` es el step-up: PIN o contraseña. */
export interface SignSheetBody {
  document?: SheetDocument;
  stage?: 'PREMATCH' | 'POSTMATCH';
  secret: string;
  signerName?: string;
  country?: string;
  deviceId?: string;
}

export interface SignSheetResponse {
  matchId: string;
  signature: ScoutEvent;
  digest: string;
  uptoSeq: number;
  document: SheetState;
}

export type SheetDelivery = 'FEDERATION' | 'TEAM_A' | 'TEAM_B' | 'ORGANISER' | 'SUPERVISOR';

export interface CloseSheetBody {
  deliveries?: SheetDelivery[];
  outcome?: SheetOutcome;
  deviceId?: string;
}

export interface CloseSheetResponse {
  matchId: string;
  closed: ScoutEvent;
  document: SheetState;
}

/** `POST /api/matches/:id/events` — el resultado del append idempotente. */
export interface AppendEventsResult {
  accepted: number;
  duplicates: string[];
  lastSeq: number;
}

// --- Usuarios y sesión -----------------------------------------------------

/**
 * Roles del padrón. Copiados de `apps/api/src/store/user-store.ts` (el portal
 * consume JSON, no importa el package del API), y con el MISMO orden que la
 * tabla de permisos: de más a menos privilegio.
 */
export const ROLES = ['admin', 'scout', 'coach', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/** Cómo se autenticó el request, según lo reporta `GET /api/auth/me`. */
export type AuthVia =
  /** Token de una sesión de usuario (`POST /api/auth/login`). */
  | 'session'
  /** `SCOUT_API_TOKEN`: el Bearer único de la federación (modo compatibilidad). */
  | 'legacy-token'
  /** `SCOUT_DEV_OPEN=1`: API abierto de dev/LAN, sin credenciales. */
  | 'open'
  /**
   * Credencial de PARTIDO (QR+PIN, §4.3): no es una cuenta del padrón y la
   * sesión está acotada a un solo partido. Es con lo que entra el banco, el
   * árbitro del sábado y la pantalla de estadio.
   */
  | 'match-credential';

/** Alcance de partido de una sesión que salió de una credencial QR+PIN. */
export interface MatchScope {
  matchId: string;
  role: MatchRole;
  team?: 'home' | 'away';
  holderName: string;
}

/** `GET /api/auth/me` — quién soy PARA EL API (no lo que dice el localStorage). */
export interface Identity {
  id: string;
  username: string;
  name: string;
  role: Role;
  via: AuthVia;
  /**
   * Organización ACTIVA de la sesión. El API lo manda desde el multi-tenant;
   * la mesa lo necesita para armar la URL del QR (`benchCredentialUrl` exige
   * el `org` porque el canje `POST /api/auth/match-login` lo pide).
   */
  orgId?: string;
  /**
   * Sólo con credencial de partido. Es lo que permite que una tablet de banco
   * que se recarga en medio del set sepa de qué partido es y con qué rol, sin
   * volver a escanear el QR.
   */
  match?: MatchScope;
  /** Permisos de esa credencial EN ESE PARTIDO (`intents:write`, …). */
  permissions?: string[];
}

/** `POST /api/auth/login` — el token se muestra una vez y lo guarda el cliente. */
export interface LoginResult {
  token: string;
  user: { id: string; username: string; name: string; role: Role };
}

/** Fila del padrón (`GET /api/users`). Nunca trae el hash de la contraseña. */
export interface PublicUser {
  id: string;
  username: string;
  name: string;
  role: Role;
  createdAt: string;
  disabled: boolean;
}

/** Cuerpo de `PUT /api/users/:username`. Al crear, `password` es obligatoria. */
export interface UserUpsert {
  name: string;
  role: Role;
  password?: string;
  disabled?: boolean;
}

/** Mínimo que hace falta para reescribir un usuario sin tocarle nada más. */
export type UserIdentityFields = Pick<PublicUser, 'name' | 'role'>;

// --- Competencias ----------------------------------------------------------

/** `GET /api/competitions/:id/fixture` */
export interface FixturePage {
  competitionId: string;
  count: number;
  fixture: FixtureMatch[];
}

/**
 * Un bloque de la tabla (una fase, o un grupo de una fase). La forma se copia
 * de `apps/api/src/competition/standings-view.ts`: el portal consume JSON, no
 * importa el módulo del API. Lo que sí se importa como TIPO es `StandingsRow`,
 * que vive en el contrato compartido.
 */
export interface StandingsBlock {
  phaseId: string;
  phaseName: string;
  kind: 'regular' | 'groups' | 'playoff';
  groupId?: string;
  groupName?: string;
  rows: StandingsRow[];
  scoring: ScoringSystem;
  tiebreakers: TiebreakCriterion[];
  matchesCounted: number;
  matchesPending: number;
}

/** `GET /api/competitions/:id/standings` */
export interface StandingsResponse {
  competitionId: string;
  name: string;
  season: string;
  generatedAt: string;
  blocks: StandingsBlock[];
}

/** Cuerpo de `POST /api/competitions/:id/fixture/generate`. */
export interface GenerateFixtureInput {
  phaseId: string;
  format?: 'round-robin' | 'groups' | 'knockout';
  legs?: 1 | 2;
  startMatchday?: number;
  thirdPlace?: boolean;
  teamIds?: string[];
}

// --- SeasonReport: forma copiada de packages/stats/src/season-report.ts -----

export interface SeasonPlayerReport extends PlayerReport {
  /** En cuántos partidos del agregado apareció. */
  matches: number;
}

export interface SeasonTeamReport {
  name: string;
  skills: Partial<Record<string, SkillLine>>;
  players: SeasonPlayerReport[];
  /** Ponderado por rallies: `Σ sideoutPoints / Σ ralliesReceived`. */
  sideoutPct: number;
  /** Ponderado por rallies: `Σ breakPoints / Σ ralliesServed`. */
  breakPct: number;
  ralliesServed: number;
  ralliesReceived: number;
  sideoutPoints: number;
  breakPoints: number;
  pointsByReason: Record<string, number>;
  matches: number;
}

export interface SeasonMatchRef {
  matchId: string;
  name: string;
}

export interface SeasonReport {
  generatedAt: string;
  setsIncluded: number[] | 'all';
  matches: SeasonMatchRef[];
  teams: SeasonTeamReport[];
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

/** Código estable para que la UI decida (banner vs. vacío vs. link a Ajustes). */
export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'not_implemented'
  | 'bad_request'
  /** 423: la cuenta existe y la clave es correcta, pero está deshabilitada. */
  | 'disabled'
  /** 429: freno de fuerza bruta del login. */
  | 'rate_limited'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  constructor(
    public readonly kind: ApiErrorKind,
    message: string,
    /** Status HTTP, o 0 si nunca hubo respuesta. */
    public readonly status = 0,
    /**
     * Sólo en los 403: el permiso que el API pidió (`events:read`, `export:dvw`).
     * Es un id técnico del backend, NO se traduce: sirve para que el usuario le
     * pida a la federación exactamente eso.
     */
    public readonly requiredPermission: string | null = null,
    /**
     * El campo `error` del cuerpo (`invalid_credentials`, `user_disabled`,
     * `cannot_delete_self`…). Id técnico y estable del API: la UI lo usa para
     * distinguir dos 401 que NO son lo mismo (login rechazado vs. sesión
     * vencida) sin parsear mensajes.
     */
    public readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Cuerpo de error del API (`{ error, message }`; en los 403 también `required`). */
interface ApiErrorBody {
  error?: string;
  message?: string;
  required?: string;
}

/**
 * `true` si el API contestó 403 (el rol del usuario no alcanza).
 *
 * Es distinto de un 401: el token está bien, lo que falta es permiso, así que
 * reintentar o revisar Ajustes no arregla nada. La UI lo usa para DEGRADAR
 * (mostrar lo que sí puede ver) en vez de plantar un cartel rojo.
 */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.kind === 'forbidden';
}

/** Traduce status + cuerpo a un error con mensaje mostrable. */
export function describeHttpError(status: number, body: ApiErrorBody | null): ApiError {
  const detail = typeof body?.message === 'string' && body.message.trim() !== '' ? body.message : '';
  const code = typeof body?.error === 'string' && body.error.trim() !== '' ? body.error : null;
  const t = getT();
  switch (status) {
    case 400:
      return new ApiError('bad_request', detail || t('api.badRequest'), status, null, code);
    case 401:
      // Dos 401 bien distintos: el del LOGIN (usuario o clave mal) y el de
      // cualquier otro endpoint (falta credencial o la sesión ya no vale).
      // Mandar al usuario a "revisá el token en Ajustes" cuando lo que hizo fue
      // errarle a la contraseña es el clásico mensaje que no ayuda a nadie.
      return code === 'invalid_credentials'
        ? new ApiError('unauthorized', t('auth.badCredentials'), status, null, code)
        : new ApiError('unauthorized', t('api.unauthorized'), status, null, code);
    case 403: {
      // El mensaje del API es técnico y viene siempre en castellano ("El rol
      // viewer no tiene el permiso events:read"): se ignora a propósito y se
      // arma uno traducido, conservando el id del permiso que sí es útil.
      const required = typeof body?.required === 'string' && body.required.trim() !== '' ? body.required : null;
      const message = required === null ? t('api.forbidden') : t('api.forbiddenPermission', { permission: required });
      return new ApiError('forbidden', message, status, required, code);
    }
    case 404:
      return new ApiError('not_found', detail || t('api.notFound'), status, null, code);
    case 409:
      // El 409 del padrón («no podés borrarte a vos mismo») trae su propio
      // mensaje y no tiene nada que ver con el partido incompleto.
      return new ApiError('bad_request', detail || t('api.conflict'), status, null, code);
    case 422:
      return new ApiError('bad_request', detail || t('api.unprocessable'), status, null, code);
    case 423:
      return new ApiError('disabled', t('auth.accountDisabled'), status, null, code);
    case 429:
      return new ApiError('rate_limited', t('auth.tooManyAttempts'), status, null, code);
    case 501:
      return new ApiError('not_implemented', detail || t('api.notImplemented'), status, null, code);
    default:
      if (status >= 500) {
        return new ApiError('server', detail || t('api.serverError', { status }), status, null, code);
      }
      return new ApiError('unknown', detail || t('api.unexpected', { status }), status, null, code);
  }
}

// ---------------------------------------------------------------------------
// Cliente
// ---------------------------------------------------------------------------

export interface ApiTarget {
  /** Base del API; vacío = mismo origen que el portal. */
  baseUrl: string;
  /** Bearer token; vacío = modo dev abierto. */
  token: string;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  /** Se lee en cada llamada: cambiar Ajustes no obliga a recrear el cliente. */
  getTarget: () => ApiTarget;
  fetchImpl?: FetchLike;
  /**
   * Se llama cuando un pedido AUTENTICADO recibe 401, o sea: la credencial que
   * estábamos mandando ya no vale (sesión vencida o revocada desde el padrón).
   * Lo usa `App.tsx` para cortar la sesión y mostrar el login en vez de dejar
   * la pantalla llena de errores que el usuario no puede resolver.
   *
   * El 401 del propio login NO pasa por acá: ese pedido va anónimo.
   */
  onUnauthorized?: () => void;
}

export type QueryValue = string | number | readonly number[] | readonly string[] | undefined;

export interface RequestOptions {
  /**
   * No manda el header de autorización. Es para `POST /api/auth/login`: si el
   * token guardado venció, mandarlo sólo ensucia la auditoría del server y
   * confunde el diagnóstico ("¿me rechazó la clave o el token viejo?").
   */
  anonymous?: boolean;
}

/** `{ sets: [1,2] }` → `?sets=1,2`; vacíos y `undefined` se omiten. */
export function buildQuery(params: Record<string, QueryValue>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    const raw = Array.isArray(value) ? value.join(',') : String(value);
    if (raw === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(raw)}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/** `base` + `path` sin barras duplicadas ni base vacía rota. */
export function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export class ApiClient {
  private readonly fetchImpl: FetchLike;

  constructor(private readonly options: ApiClientOptions) {
    const fallback: FetchLike = (input, init) => {
      const g = globalThis as { fetch?: FetchLike };
      if (!g.fetch) return Promise.reject(new ApiError('network', getT()('api.noFetch')));
      return g.fetch(input, init);
    };
    this.fetchImpl = options.fetchImpl ?? fallback;
  }

  get target(): ApiTarget {
    return this.options.getTarget();
  }

  /** URL absoluta (o relativa al origen, si no hay baseUrl) de un path del API. */
  url(path: string, query: Record<string, QueryValue> = {}): string {
    return joinUrl(this.target.baseUrl, path) + buildQuery(query);
  }

  /** Headers de autenticación; sin token no se manda nada (modo dev abierto). */
  authHeaders(): Record<string, string> {
    const token = this.target.token.trim();
    return token === '' ? {} : { authorization: `Bearer ${token}` };
  }

  /** Pedido crudo: agrega auth y traduce fallos de red, sin parsear el cuerpo. */
  async raw(
    path: string,
    query: Record<string, QueryValue> = {},
    init: RequestInit = {},
    options: RequestOptions = {},
  ): Promise<Response> {
    const url = this.url(path, query);
    const anonymous = options.anonymous === true;
    const auth = anonymous ? {} : this.authHeaders();
    const headers = { ...auth, ...((init.headers as Record<string, string>) ?? {}) };
    try {
      const response = await this.fetchImpl(url, { ...init, headers });
      if (response.status === 401 && !anonymous) this.options.onUnauthorized?.();
      return response;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new ApiError('network', getT()('api.networkError', { reason }));
    }
  }

  /** Pedido que espera JSON; cualquier status ≥ 400 sale como `ApiError`. */
  async request<T>(
    path: string,
    query: Record<string, QueryValue> = {},
    init: RequestInit = {},
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.raw(path, query, init, options);
    if (!response.ok) throw describeHttpError(response.status, await readErrorBody(response));
    if (response.status === 204) return undefined as T;
    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError('unknown', getT()('api.notJson'), response.status);
    }
  }

  // --- Salud ---------------------------------------------------------------

  health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/api/health');
  }

  // --- Sesión --------------------------------------------------------------

  /**
   * Login contra el padrón de la federación. Devuelve el token OPACO de la
   * sesión (no un JWT): el server guarda su hash y lo puede revocar, así que
   * echar a alguien surte efecto al instante.
   *
   * Va SIN el header de autorización a propósito (ver `RequestOptions`).
   */
  login(username: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>(
      '/api/auth/login',
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username, password }),
      },
      { anonymous: true },
    );
  }

  /** Revoca en el server el token con el que viaja este pedido. Idempotente. */
  logout(): Promise<{ ok: boolean; revoked: boolean }> {
    return this.request<{ ok: boolean; revoked: boolean }>('/api/auth/logout', {}, { method: 'POST' });
  }

  /**
   * Quién soy PARA EL API. Es la única fuente de verdad de la identidad: el
   * localStorage dice lo que se guardó al loguearse, esto dice si el token
   * todavía vale y con qué rol (si un admin te bajó a viewer, se entera acá).
   *
   * `via` distingue los tres modos: sesión real, Bearer manual de compatibilidad
   * o API abierto de dev.
   */
  me(): Promise<Identity> {
    return this.request<Identity>('/api/auth/me');
  }

  // --- Padrón de usuarios (sólo rol admin: el API pide `users:manage`) ------

  listUsers(): Promise<PublicUser[]> {
    return this.request<PublicUser[]>('/api/users');
  }

  /**
   * PUT idempotente: crea o reemplaza. Al crear, `password` es obligatoria
   * (una cuenta sin credencial no se puede usar); al editar es opcional y, si
   * viene, el API REVOCA todas las sesiones abiertas de esa persona.
   */
  saveUser(username: string, input: UserUpsert): Promise<PublicUser> {
    return this.request<PublicUser>(
      `/api/users/${encodeURIComponent(username)}`,
      {},
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  async deleteUser(username: string): Promise<void> {
    await this.request<void>(`/api/users/${encodeURIComponent(username)}`, {}, { method: 'DELETE' });
  }

  /**
   * Cierra TODAS las sesiones abiertas de un usuario, sin cambiarle la clave.
   *
   * RODEO DECLARADO: el API no tiene (todavía) una ruta de revocación pura. Lo
   * que sí revoca es `disabled: true`, así que esto es bloquear + volver a
   * habilitar. Consecuencias que conviene conocer:
   *  - quedan DOS entradas de auditoría (`user_update` × 2), no una;
   *  - entre las dos llamadas la cuenta está bloqueada por unos milisegundos;
   *  - si la segunda falla, el usuario QUEDA BLOQUEADO: por eso el error lo
   *    dice con todas las letras en vez de un "falló" genérico.
   * Cuando el API sume `DELETE /api/users/:username/sessions`, esto pasa a ser
   * una sola llamada y el rodeo desaparece de acá, no de la UI.
   */
  async revokeUserSessions(username: string, user: UserIdentityFields): Promise<PublicUser> {
    await this.saveUser(username, { name: user.name, role: user.role, disabled: true });
    try {
      return await this.saveUser(username, { name: user.name, role: user.role, disabled: false });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new ApiError(
        err instanceof ApiError ? err.kind : 'unknown',
        getT()('users.revokeHalfway', { username, reason }),
        err instanceof ApiError ? err.status : 0,
      );
    }
  }

  // --- Partidos ------------------------------------------------------------

  listMatches(): Promise<MatchSummary[]> {
    return this.request<MatchSummary[]>('/api/matches');
  }

  /** `sinceSeq` es EXCLUSIVO: mandá el último seq que ya tenés. */
  getEvents(matchId: string, sinceSeq?: number): Promise<EventsPage> {
    return this.request<EventsPage>(`/api/matches/${encodeURIComponent(matchId)}/events`, {
      sinceSeq,
    });
  }

  getMatchReport(
    matchId: string,
    sets?: readonly number[],
    opts: ReportQueryOptions = {},
  ): Promise<MatchReport> {
    return this.request<MatchReport>(`/api/matches/${encodeURIComponent(matchId)}/report`, {
      sets,
      efficiency: opts.efficiency,
      ...previousRallyQuery(opts.previousRally),
    });
  }

  /**
   * Playlist de clips: los CORTES que matchean el filtro, no el video.
   *
   * El archivo lo produce el server de replay VAR de la cancha, al que el
   * portal le habla directo por LAN (ADR-017); acá sólo viene la lista de
   * `{videoNs, pre, post, label}` que sale del log.
   */
  getClipPlaylist(matchId: string, filter: ClipFilterQuery = {}): Promise<ClipPlaylistResponse> {
    return this.request<ClipPlaylistResponse>(
      `/api/matches/${encodeURIComponent(matchId)}/clips`,
      {
        team: filter.team,
        player: filter.player,
        skill: filter.skill,
        evaluation: filter.evaluation,
        sets: filter.sets,
        rotation: filter.rotation,
        rotationTeam: filter.rotationTeam,
        phase: filter.phase,
        phaseTeam: filter.phaseTeam,
        ...previousRallyQuery(filter.previousRally),
      },
    );
  }

  /**
   * URL del REPORTE ESTADÍSTICO imprimible (fundamentos, sideout, jugadores).
   * Para abrir en pestaña nueva si no hay token; con token va por `download()`.
   */
  reportHtmlUrl(matchId: string, sets?: readonly number[]): string {
    return this.url(`/api/matches/${encodeURIComponent(matchId)}/report.html`, { sets });
  }

  /**
   * URL de la PLANILLA (acta de mesa): otro documento, no el reporte.
   * No acepta `?sets=`: el acta es del partido entero, como la de papel.
   */
  scoresheetHtmlUrl(matchId: string): string {
    return this.url(`/api/matches/${encodeURIComponent(matchId)}/scoresheet.html`);
  }

  dvwUrl(matchId: string): string {
    return this.url(`/api/matches/${encodeURIComponent(matchId)}/export/dvw`);
  }

  /** Endpoint SSE del live score (lo consume `src/lib/live.ts`). */
  liveUrl(matchId: string): string {
    return this.url(`/api/matches/${encodeURIComponent(matchId)}/live`);
  }

  // --- Fase D: consola de partido y cola de intents -------------------------

  /**
   * La proyección que miran las cinco pantallas del bench (§5.4): marcador,
   * rotación, cupos, cancha, rallies y la cola.
   *
   * Es un documento DIGERIDO y pide sólo `matches:read`, así que una credencial
   * de pantalla de estadio —que no puede leer el log— igual dibuja el
   * marcador correcto. Por eso el bench no deriva nada del SSE: **el stream es
   * el pulso, esto es la verdad**.
   */
  getBenchSnapshot(matchId: string): Promise<BenchSnapshot> {
    return this.request<BenchSnapshot>(`/api/matches/${encodeURIComponent(matchId)}/bench`);
  }

  /**
   * Poll incremental de la cola. Con `sinceVersion` la respuesta normal es
   * cero filas: es la razón por la que polear no cuesta nada.
   */
  listIntents(matchId: string, sinceVersion?: number): Promise<IntentsPage> {
    return this.request<IntentsPage>(`/api/matches/${encodeURIComponent(matchId)}/intents`, {
      sinceVersion,
    });
  }

  /** El banco pide. Devuelve el intent PENDIENTE; el evento no existe todavía. */
  createIntent(matchId: string, body: CreateIntentBody): Promise<{ intent: BenchIntent }> {
    return this.request<{ intent: BenchIntent }>(
      `/api/matches/${encodeURIComponent(matchId)}/intents`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  /** La mesa atiende. Con `ACCEPT` nace el evento canónico y viene en la respuesta. */
  resolveIntent(
    matchId: string,
    intentId: string,
    decision: 'ACCEPT' | 'REJECT',
    reason?: string,
  ): Promise<{ intent: BenchIntent; event?: ScoutEvent }> {
    return this.request<{ intent: BenchIntent; event?: ScoutEvent }>(
      `/api/matches/${encodeURIComponent(matchId)}/intents/${encodeURIComponent(intentId)}/resolve`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ decision, ...(reason !== undefined ? { reason } : {}) }),
      },
    );
  }

  /** El banco retira SU pedido (la mesa no lo atendió todavía). */
  cancelIntent(matchId: string, intentId: string): Promise<{ intent: BenchIntent }> {
    return this.request<{ intent: BenchIntent }>(
      `/api/matches/${encodeURIComponent(matchId)}/intents/${encodeURIComponent(intentId)}`,
      {},
      { method: 'DELETE' },
    );
  }

  /**
   * Canje del QR de una credencial de partido (§4.3). Va SIN el header de
   * autorización, igual que el login: es la ruta que EMITE la credencial.
   */
  matchLogin(body: MatchLoginBody): Promise<MatchLoginResult> {
    return this.request<MatchLoginResult>(
      '/api/auth/match-login',
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      { anonymous: true },
    );
  }

  // --- Acta electrónica: la mesa de control (`routes/sheet.ts`) -------------

  /**
   * El estado del acta de un vistazo: fase del documento, oficiales, firmas,
   * challenge y designaciones. Es la proyección que dibuja la pantalla de la
   * mesa (`#/mesa/:matchId`), no una tabla.
   */
  getSheet(matchId: string): Promise<SheetResponse> {
    return this.request<SheetResponse>(`/api/matches/${encodeURIComponent(matchId)}/sheet`);
  }

  listCredentials(matchId: string): Promise<{ matchId: string; credentials: MatchCredential[] }> {
    return this.request<{ matchId: string; credentials: MatchCredential[] }>(
      `/api/matches/${encodeURIComponent(matchId)}/credentials`,
    );
  }

  /**
   * Emite una credencial de partido (QR+PIN) o designa una cuenta del padrón.
   * El token y el PIN de una credencial vienen UNA sola vez en la respuesta:
   * la pantalla los muestra ahí mismo y no existen en claro en ningún otro
   * lado (§4.3).
   */
  issueCredential(matchId: string, body: IssueCredentialBody): Promise<IssuedCredentialResult> {
    return this.request<IssuedCredentialResult>(
      `/api/matches/${encodeURIComponent(matchId)}/credentials`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  revokeCredential(
    matchId: string,
    credentialId: string,
  ): Promise<{ ok: boolean; revoked: boolean }> {
    return this.request<{ ok: boolean; revoked: boolean }>(
      `/api/matches/${encodeURIComponent(matchId)}/credentials/${encodeURIComponent(credentialId)}`,
      {},
      { method: 'DELETE' },
    );
  }

  /**
   * Firma con step-up (`POST /sign`). El que firma es EL DUEÑO DE LA SESIÓN
   * que viaja en el request, no un campo del body: por eso `asToken` permite
   * firmar con la sesión de la credencial del titular (canjeada con
   * `matchLogin`) sin pisar la sesión del anotador que opera la mesa.
   *
   * Va como pedido "anónimo" con el header puesto A MANO, y no es un capricho:
   * un 401 acá significa "el PIN/la contraseña no es correcta" (step-up
   * fallido), no "tu sesión venció". Si pasara por `authHeaders()`, ese 401
   * dispararía `onUnauthorized` y el portal echaría al anotador al login
   * porque el árbitro le erró al PIN — exactamente lo contrario de una
   * pantalla imposible de operar mal.
   */
  signSheet(
    matchId: string,
    body: SignSheetBody,
    options: { asToken?: string } = {},
  ): Promise<SignSheetResponse> {
    const token = options.asToken ?? this.target.token.trim();
    return this.request<SignSheetResponse>(
      `/api/matches/${encodeURIComponent(matchId)}/sign`,
      {},
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token !== '' ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      },
      { anonymous: true },
    );
  }

  /** Cierra el acta (`POST /sheet/close`): congela el documento firmado. */
  closeSheet(matchId: string, body: CloseSheetBody = {}): Promise<CloseSheetResponse> {
    return this.request<CloseSheetResponse>(
      `/api/matches/${encodeURIComponent(matchId)}/sheet/close`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  /**
   * Append de eventos canónicos ya numerados (`POST /events`). Es el mismo
   * endpoint del sync de la captura: el lote es ATÓMICO (un evento inválido
   * voltea el lote entero) e idempotente por `event.id`. La mesa lo usa para
   * lo que las rutas del acta no emiten por ella: `officials_set`,
   * `challenge_resolved` y la reconciliación del marcador en un solo lote.
   */
  appendEvents(matchId: string, events: readonly ScoutEvent[]): Promise<AppendEventsResult> {
    return this.request<AppendEventsResult>(
      `/api/matches/${encodeURIComponent(matchId)}/events`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(events),
      },
    );
  }

  /**
   * Baja un archivo respetando el header de auth (un `<a href>` no lo lleva).
   * Devuelve el contenido y el nombre sugerido; quien llama arma el Blob.
   */
  async download(path: string, query: Record<string, QueryValue> = {}): Promise<{ body: string; contentType: string }> {
    const response = await this.raw(path, query);
    if (!response.ok) throw describeHttpError(response.status, await readErrorBody(response));
    return {
      body: await response.text(),
      contentType: response.headers.get('content-type') ?? 'text/plain; charset=utf-8',
    };
  }

  downloadDvw(matchId: string): Promise<{ body: string; contentType: string }> {
    return this.download(`/api/matches/${encodeURIComponent(matchId)}/export/dvw`);
  }

  /** Reporte estadístico (`report.html`): la vista del entrenador. */
  downloadReportHtml(
    matchId: string,
    sets?: readonly number[],
    efficiency?: string,
  ): Promise<{ body: string; contentType: string }> {
    return this.download(`/api/matches/${encodeURIComponent(matchId)}/report.html`, {
      sets,
      efficiency,
    });
  }

  /**
   * Planilla / acta de mesa (`scoresheet.html`): el documento oficial que se
   * imprime y se firma. Se arma sólo con el log, así que —a diferencia del
   * reporte— nunca responde 501 por falta de `@scout/stats`.
   */
  downloadScoresheetHtml(matchId: string): Promise<{ body: string; contentType: string }> {
    return this.download(`/api/matches/${encodeURIComponent(matchId)}/scoresheet.html`);
  }

  /**
   * Los dos documentos HTML del partido, elegidos por nombre.
   *
   * Existe para que la UI no vuelva a equivocarse de endpoint: el botón dice
   * qué documento quiere (`'scoresheet'` = acta, `'report'` = estadística) y
   * el mapeo a la URL vive acá, en un solo lugar y con test. `sets` se ignora
   * para el acta: es siempre del partido entero.
   */
  downloadMatchDocument(
    matchId: string,
    kind: MatchDocument,
    sets?: readonly number[],
    /** Convención de eficiencia del reporte; el acta no la usa (no tiene ratios). */
    efficiency?: string,
  ): Promise<{ body: string; contentType: string }> {
    return kind === 'scoresheet'
      ? this.downloadScoresheetHtml(matchId)
      : this.downloadReportHtml(matchId, sets, efficiency);
  }

  // --- Temporada -----------------------------------------------------------

  /** Sin `matchIds` el API toma TODOS los partidos del archivo. */
  getSeasonReport(
    matchIds?: readonly string[],
    sets?: readonly number[],
    efficiency?: string,
  ): Promise<SeasonReport> {
    return this.request<SeasonReport>('/api/season/report', {
      matchIds: matchIds && matchIds.length > 0 ? matchIds : undefined,
      sets,
      efficiency,
    });
  }

  // --- Competencias --------------------------------------------------------

  listCompetitions(): Promise<Competition[]> {
    return this.request<Competition[]>('/api/competitions');
  }

  getCompetition(competitionId: string): Promise<Competition> {
    return this.request<Competition>(`/api/competitions/${encodeURIComponent(competitionId)}`);
  }

  /** PUT idempotente: crea o reemplaza ENTERA (el API no hace merge). */
  saveCompetition(competitionId: string, input: CompetitionInput): Promise<Competition> {
    return this.request<Competition>(
      `/api/competitions/${encodeURIComponent(competitionId)}`,
      {},
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  async deleteCompetition(competitionId: string): Promise<void> {
    await this.request<void>(
      `/api/competitions/${encodeURIComponent(competitionId)}`,
      {},
      { method: 'DELETE' },
    );
  }

  /**
   * El fixture, ya puesto al día con los logs de los partidos enganchados: el
   * API recalcula lo que cambió antes de contestar (ver `competition/results.ts`).
   */
  getFixture(
    competitionId: string,
    filter: { phaseId?: string; groupId?: string; matchday?: number; teamId?: string } = {},
  ): Promise<FixturePage> {
    return this.request<FixturePage>(
      `/api/competitions/${encodeURIComponent(competitionId)}/fixture`,
      { ...filter },
    );
  }

  /** La tabla, CALCULADA de los partidos cargados (nunca se sube a mano). */
  getStandings(competitionId: string, phaseId?: string): Promise<StandingsResponse> {
    return this.request<StandingsResponse>(
      `/api/competitions/${encodeURIComponent(competitionId)}/standings`,
      { phaseId },
    );
  }

  generateFixture(competitionId: string, input: GenerateFixtureInput): Promise<FixturePage> {
    return this.request<FixturePage>(
      `/api/competitions/${encodeURIComponent(competitionId)}/fixture/generate`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  /** Partidos del archivo que todavía no están en ninguna fecha del fixture. */
  getFixtureCandidates(
    competitionId: string,
    competition?: string,
  ): Promise<{ count: number; matches: MatchSummary[] }> {
    return this.request<{ count: number; matches: MatchSummary[] }>(
      `/api/competitions/${encodeURIComponent(competitionId)}/candidates`,
      { competition },
    );
  }

  /** Engancha partidos ya scouteados a una fase (el histórico no se pierde). */
  importMatches(
    competitionId: string,
    input: { phaseId: string; matchIds: string[]; groupId?: string; matchday?: number },
  ): Promise<{
    linked: number;
    addedTeams: Array<{ teamId: string; name: string }>;
    skipped: Array<{ matchId: string; reason: string }>;
    fixture: FixtureMatch[];
  }> {
    return this.request(
      `/api/competitions/${encodeURIComponent(competitionId)}/fixture/import`,
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  // --- Equipos -------------------------------------------------------------

  listTeams(): Promise<TeamRecord[]> {
    return this.request<TeamRecord[]>('/api/teams');
  }

  getTeam(teamId: string): Promise<TeamRecord> {
    return this.request<TeamRecord>(`/api/teams/${encodeURIComponent(teamId)}`);
  }

  /** PUT idempotente: crea o reemplaza entero (el API no hace merge). */
  saveTeam(teamId: string, info: TeamInfo): Promise<TeamRecord> {
    return this.request<TeamRecord>(
      `/api/teams/${encodeURIComponent(teamId)}`,
      {},
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(info),
      },
    );
  }

  async deleteTeam(teamId: string): Promise<void> {
    await this.request<void>(`/api/teams/${encodeURIComponent(teamId)}`, {}, { method: 'DELETE' });
  }

  // --- Import de `.dvw` histórico (ítem 2.7) --------------------------------

  /**
   * Sube una tanda de archivos. `dryRun` es el PREVIEW: el API analiza lo
   * mismo que va a guardar y no escribe nada, así que lo que se muestra en
   * pantalla es literalmente lo que va a quedar.
   *
   * Los archivos van en base64 y no como texto porque **el charset lo decide
   * el server**: `file.text()` asume UTF-8 y un `.dvw` escrito en Windows
   * entraría con "Bol�var" en el padrón de equipos.
   */
  importDvw(files: readonly DvwImportFile[], dryRun: boolean): Promise<DvwImportResponse> {
    return this.request<DvwImportResponse>(
      '/api/import/dvw',
      {},
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ files, dryRun }),
      },
    );
  }
}

/** El cuerpo de error puede no ser JSON (proxy, HTML de error): nunca lanza. */
async function readErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    const text = await response.text();
    if (text.trim() === '') return null;
    return JSON.parse(text) as ApiErrorBody;
  } catch {
    return null;
  }
}
